// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { CalendarField } from 'components/molecules/CalendarField';
import { AdminTemplate } from 'components/templates/AdminTemplate';
import DOMPurify from 'dompurify';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { createAxios } from 'ts/createAxios';
import { getSlaYMDhm } from 'ts/formatDate';
import { OperatorType } from 'util/Enums';
import { formatNumberWithCommas } from 'util/commons';
import { DEFAULT_PAGE, PAGE_SIZE } from 'util/ConstantValues';
import { handleError } from 'util/errorHandler';
import TableLoading from 'components/atoms/TableLoading';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import Csv from 'util/csv';
import { API_ENDPOINTS } from 'util/endPoints';
import './Traces.scss';

type TraceListData = {
  id: string;
  datetime: string;
  userId: string;
  userName: string;
  operatorName: string;
  siteName: string;
  description: string;
  materialType: string;
  grade: string;
  weight: string;
  type: string;
};

const Traces = () => {
  const api = createAxios();
  const [traces, setTraces] = useState<TraceListData[]>([]);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [total, setTotal] = useState<number>(0);
  const [filterDate, setFilterDate] = useState('');
  const history = useHistory();
  const { t } = useTranslation();

  const fetchTraces = async (
    targetDate?: string,
    page?: number,
    forceRefresh?: boolean
  ) => {
    try {
      const params = {
        targetDate: targetDate || undefined,
        page: page,
        take: PAGE_SIZE,
      };
      const res = await api.get(API_ENDPOINTS.TRACES, { params });

      setTraces(
        forceRefresh ? res.data.traces : [...traces, ...res.data.traces]
      );
      setTotal(res.data.total);
      setCurrentPage(res.data.page);
    } catch (error) {
      handleError(error);
    }
  };

  const loadMore = () => {
    fetchTraces(filterDate, currentPage + 1, false);
  };

  useEffect(() => {
    fetchTraces(filterDate, DEFAULT_PAGE, true);
  }, [filterDate]);

  const handleDownLoadCsv = async () => {
    let tracesForCsv: TraceListData[] = [];
    try {
      const res = await api.get(API_ENDPOINTS.TRACES, {
        params: {
          page: 0,
          targetDate: filterDate || undefined,
        },
      });

      tracesForCsv = res.data.traces;
    } catch (error) {
      handleError(error);
    }

    new Csv({
      headers: [
        ['datetime', t('traces.thead_date')],
        ['userName', t('traces.thead_thead_person')],
        ['recycleSiteName', t('traces.thead_manufacturer')],
        ['compressionsSiteName', t('traces.thead_intermediate_processing')],
        ['producerSiteName', t('traces.thead_producer')],
        ['materialType', t('traces.thead_material_name')],
        ['kinds', t('traces.thead_kinds')],
        ['grade', t('traces.thead_quality')],
        ['weight', t('traces.thead_weight')],
        ['id', t('traces.thead_id')],
      ],
      data:
        tracesForCsv.map((trace) => ({
          datetime: getSlaYMDhm(trace.datetime),
          userName: trace.userId + '(' + trace.userName + ')',
          recycleSiteName: `${
            trace.type === OperatorType.Recycle ? trace.siteName : ''
          }`,
          compressionsSiteName: `${
            trace.type === OperatorType.Compressions ? trace.siteName : ''
          }`,
          producerSiteName: `${
            trace.type === OperatorType.Producer ? trace.siteName : ''
          }`,
          materialType: trace.description,
          kinds: trace.materialType,
          grade: trace.grade,
          weight: formatNumberWithCommas(trace.weight) + 'kg',
          id: trace.id,
        })) || [],
    }).downLoadCsv({
      filename: t('common.csv_file_name'),
    });
  };

  return (
    <AdminTemplate
      h1={t('common.sidebar_trace')}
      active="トレーサビリティ"
      breadcrumb={[{ label: t('common.sidebar_trace') }]}
      className="traces-page"
    >
      <div
        className={'countList'}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            t('common.page', {
              page: total || 0,
            }) ?? ''
          ),
        }}
      />
      <div className="search-area">
        <CalendarField
          maxDate={new Date()}
          onChange={(value) => {
            setFilterDate(value);
          }}
        />
        <button className="csv-button" onClick={handleDownLoadCsv}>
          <IconDownload />
          {t('common.csv_download')}
        </button>
      </div>
      <div className="scroll-wrapper">
        <table id="trace-table">
          <thead>
            <tr>
              <th className="c--date">{t('traces.thead_date')}</th>
              <th className="c--staff">{t('traces.thead_thead_person')}</th>
              <th className="c--operator">{t('traces.thead_manufacturer')}</th>
              <th className="c--operator">
                {t('traces.thead_intermediate_processing')}
              </th>
              <th className="c--description">{t('traces.thead_producer')}</th>
              <th className="c--description">
                {t('traces.thead_material_name')}
              </th>
              <th className="c--type">{t('traces.thead_kinds')}</th>
              <th className="c--grade">{t('traces.thead_quality')}</th>
              <th className="c--weight">{t('traces.thead_weight')}</th>
              <th className="c--id">{t('traces.thead_id')}</th>
            </tr>
          </thead>
          <tbody>
            {traces && traces.length > 0 ? (
              traces.map((item, i) => {
                return (
                  <tr
                    key={i}
                    onClick={() => {
                      history.push('/traces/' + item.id);
                    }}
                  >
                    <td className="c--date">
                      <p>{getSlaYMDhm(item.datetime) || '-'}</p>
                    </td>
                    <td className="left c--staff">
                      <p>{`${item.userId}${
                        item.userName ? ` (${item.userName})` : ''
                      }`}</p>
                    </td>
                    <td className="left c--operator">
                      <p>
                        {item.type === OperatorType.Recycle
                          ? item.siteName
                          : '-'}
                      </p>
                    </td>
                    <td className="left c--operator">
                      <p>
                        {item.type === OperatorType.Compressions
                          ? item.siteName
                          : '-'}
                      </p>
                    </td>
                    <td className="left c--description">
                      <p>
                        {item.type === OperatorType.Producer
                          ? item.siteName
                          : '-'}
                      </p>
                    </td>
                    <td className="left c--description">
                      <p>{item.description || '-'}</p>
                    </td>
                    <td className="left c--type">
                      <p>{item.materialType || '-'}</p>
                    </td>
                    <td className="left c--grade">
                      <p>{item.grade || '-'}</p>
                    </td>
                    <td className="left c--weight">
                      <p>
                        {item.weight
                          ? formatNumberWithCommas(item.weight)
                          : '-'}
                        kg
                      </p>
                    </td>
                    <td className="left c--id">
                      <p>{item.id || '-'}</p>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="msg-error">{t('messages.M_008')}</td>
              </tr>
            )}
          </tbody>
          {traces && traces.length > 0 && (
            <TableLoading
              onNext={() => {
                if (traces.length >= PAGE_SIZE && traces.length < total) {
                  setCurrentPage(currentPage + 1);
                  loadMore();
                }
              }}
            />
          )}
        </table>
      </div>
    </AdminTemplate>
  );
};

export default Traces;
